<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';

import { Buffer } from "buffer";
import { VList } from "virtua/vue";
import { onMounted, ref } from "vue";
import { useRoute } from "vue-router";

import { caseFileService } from "@/services";

import { useAnnotationStore } from "@/stores/annotation-store";

import RsViewerPageRenderer from "../ViewerPageRenderer.vue";
import { VIEWER_PAGE_PADDING, PANEL_SIZE } from "../../constants";

import type { SearchResult, ViewerPage } from "@/models/Viewer";
import type { ParsedMark } from "@/models/Mark";
import type { HighlightAnnotation, RectangleAnnotation } from "@/models/Annotation";
import { isHighlightAnnotation, isRectangleAnnotation } from "@/services/UtilitiesService";

interface Scale {
  width: number;
  height: number;
}
type PageStatus = "INIT" | "LOADING" | "SUCCESS" | "ERROR";

withDefaults(defineProps<{
  searchResults: SearchResult[];
  session: string;
  fileReference: string;
  totalPages: number;
}>(), {  })

const $route = useRoute();
const thumbnailsRef = ref<HTMLElement | null>(null);
let pages = _ref<ViewerPage[]>([]);
const zoomLevel = ref<"large" | "medium" | "small">("large");

const width = _computed(() => {
  switch (zoomLevel.value) {
    case "small":
      return 110;
    case "medium":
      return 190;
    case "large":
    default:
      return PANEL_SIZE - VIEWER_PAGE_PADDING / 2;
  }
});

const annotations = _computed(() => {
  return useAnnotationStore().annotations.filter(
    (a): a is ParsedMark<RectangleAnnotation | HighlightAnnotation> =>
      a.fileReference === __props.fileReference &&
      (isRectangleAnnotation(a.parsedMarkData) || isHighlightAnnotation(a.parsedMarkData)),
  );
});

const params = _computed(() => {
  return $route.params;
});

const pageNumber = _computed(() => {
  return +(params.value.filepage || selectedAnnotation.value?.pageNumber || 0);
});

const selectedAnnotation = _computed(() => {
  if (!params.value.annotation || params.value.casefile !== __props.fileReference) return null;
  return annotations.value.find((a) => a.markId === params.value.annotation)?.parsedMarkData;
});

onMounted(() => {
  for (let i = 1; i <= __props.totalPages; i++) {
    pages.value.push({
      pageNumber: i,
      svg: "",
      dimensions: { height: PANEL_SIZE, width: width.value },
      status: "INIT",
      textLines: [],
    });
  }
});

function pageUpdated(payload: { pageNumber: number; svg?: string; dimensions?: Scale; status?: PageStatus }) {
  const page = pages.value[payload.pageNumber - 1];
  pages.value[payload.pageNumber - 1] = {
    ...page,
    ...payload,
  };

  pages.value = [...pages.value];
  if (payload.status !== "SUCCESS") return;
}

async function loadPage(page: number) {
  pageUpdated({ pageNumber: page, status: "LOADING" });
  try {
    const imageBuffer = await caseFileService.getDocumentThumbnail(__props.session, page - 1);
    const base64 = "data:image/jpeg;base64," + Buffer.from(imageBuffer, "binary").toString("base64");
    const i = new Image();
    i.src = base64;
    i.onload = () => {
      pageUpdated({
        pageNumber: page,
        svg: base64,
        dimensions: { width: i.width, height: i.height },
        status: "SUCCESS",
      });
    };
  } catch (e) {
    console.log(e);
    pageUpdated({ pageNumber: page, status: "ERROR" });
  }
}

function getPageScale(p: number) {
  const page = pages.value[p - 1];
  if (!page.svg) return 1;
  return (width.value - VIEWER_PAGE_PADDING) / page.dimensions.width;
}
</script>
<template>
  <div :style="{ width: PANEL_SIZE + 'px' }" class="viewer-views-panel">
    <div class="tw-flex tw-items-center tw-justify-center">
      <button
        v-for="(level, idx) in ['small', 'medium', 'large'] satisfies Array<typeof zoomLevel>"
        :key="level"
        class="tw-btn-md-gray !tw-rounded-none tw-capitalize"
        :class="{ 'tw-btn-gray-active': zoomLevel === level, '!tw-rounded-l': idx === 0, '!tw-rounded-r': idx === 2 }"
        @click="zoomLevel = level"
      >
        {{ level }}
      </button>
    </div>
    <div ref="thumbnailsRef" class="tw-mt-2 tw-w-full tw-flex-1 tw-overflow-auto">
      <VList
        ref="viewer"
        v-slot="{ item }: { item: ViewerPage }"
        :data="pages"
        :item-size="PANEL_SIZE"
        class="tw-h-full"
      >
        <RsViewerPageRenderer
          :key="item.pageNumber"
          current-mouse-tool="THUMBNAILS"
          :session="session"
          :viewer-scale="{ width, height: PANEL_SIZE }"
          :page-number="item.pageNumber"
          :page-scale="getPageScale(item.pageNumber)"
          :file-reference="fileReference"
          :annotations="annotations.filter((a) => a.parsedMarkData.pageNumber === item.pageNumber)"
          :svg="item.svg"
          :dimensions="item.dimensions"
          :text-lines="item.textLines"
          :search-results="searchResults.filter((sr) => +sr.pageIndex + 1 === +item.pageNumber)"
          :page-status="item.status"
          :selected-search="undefined"
          :selected-area="undefined"
          :class="{ 'tw-shadow-dark-gray': pageNumber === +item.pageNumber }"
          @load-page="() => loadPage(item.pageNumber)"
        />
      </VList>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.viewer-views-panel {
  padding: 12px 8px 2px 8px;
  display: flex;
  flex-direction: column;
}
</style>
